import type { PreparedRun } from "@/Features/Labels/Modals/PrintLabelsModal";

import type { Evidence } from "@/Features/Evidence/evidence.types";
import {
  NO_CASE_ACCESS,
  buildEvidenceItemInfo,
  loadLabelLookups,
  refreshFailedLookups,
} from "./evidenceLabelLookups";
import type { LabelLookups } from "./evidenceLabelLookups";

/*
  What PrintLabelsModal needs to print evidence labels, shared by bulk print
  (the evidence table) and single item print (the detail page).
*/

/** Case numbers named in a failure message before it says "and N more". */
const NAMED_CASES = 3;

/** "case A", "cases A and B", "cases A, B and C", "cases A, B, C and 2 more". */
const describeCases = (labels: string[]): string => {
  if (labels.length === 1) return `case ${labels[0]}`;
  const named = labels.slice(0, NAMED_CASES);
  const rest = labels.length - named.length;
  if (rest > 0) return `cases ${named.join(", ")} and ${rest} more`;
  return `cases ${named.slice(0, -1).join(", ")} and ${
    named[named.length - 1]
  }`;
};

/**
 * Loads the lookups for a run and checks every item can be filled in, before
 * anything is sent. Never throws.
 *
 * - No `previous` (a first send, a preview, or a different template): a full
 *   `loadLabelLookups`.
 * - `previous` (Try Again on the same template): `refreshFailedLookups`, which
 *   refetches only the cases that failed and leaves no access ones alone.
 *
 * A case the user can't open stops the run, and Try Again can't fix it. Any
 * other failed lookup stops it too, but Try Again can.
 */
export const prepareEvidenceLabelRun = async (
  items: Evidence[],
  labelXml: string,
  previous?: LabelLookups
): Promise<PreparedRun<LabelLookups>> => {
  let lookups: LabelLookups;
  try {
    lookups = previous?.orgInfo
      ? await refreshFailedLookups(previous, items, labelXml)
      : await loadLabelLookups(items, labelXml);
  } catch (error) {
    return {
      ok: false,
      retryable: true,
      message:
        error instanceof Error && error.message
          ? error.message
          : "Couldn't load the organization info",
    };
  }

  // Every item, in print order, not just the one being previewed.
  const failed = new Map<number, string>();
  items.forEach((item) => {
    if (
      lookups.failedCaseIds[item.case_id] !== undefined &&
      !failed.has(item.case_id)
    ) {
      failed.set(item.case_id, String(item.case_number || item.case_id));
    }
  });

  const noAccess = Array.from(failed.entries())
    .filter(([caseId]) => lookups.failedCaseIds[caseId] === NO_CASE_ACCESS)
    .map(([, label]) => label);

  if (noAccess.length > 0) {
    const one = noAccess.length === 1;
    return {
      ok: false,
      retryable: false,
      message: `You don't have access to ${describeCases(noAccess)}, so ${
        one ? "its" : "their"
      } labels can't be filled in`,
      // Single item print has no selection to change.
      hint:
        items.length === 1
          ? "Pick a template without case placeholders."
          : `Remove ${
              one ? "that case's" : "those cases'"
            } items from the selection, or pick a template without case placeholders.`,
      lookups,
    };
  }

  if (failed.size > 0) {
    return {
      ok: false,
      retryable: true,
      message: `Couldn't load case data for ${describeCases(
        Array.from(failed.values())
      )}`,
      lookups,
    };
  }

  return { ok: true, lookups, orgInfo: lookups.orgInfo };
};

export type EvidenceLabelAdapter = {
  resolveItems: () => Promise<Evidence[]>;
  itemKey: (item: Evidence) => string;
  itemLabel: (item: Evidence) => string;
  prepareRun: typeof prepareEvidenceLabelRun;
  buildItemInfo: typeof buildEvidenceItemInfo;
};

/** The PrintLabelsModal props for evidence items from `resolveItems`. */
export const createEvidenceLabelAdapter = (
  resolveItems: () => Promise<Evidence[]>
): EvidenceLabelAdapter => ({
  resolveItems,
  itemKey: (item) => item.uuid,
  itemLabel: (item) => item.evidence_number,
  prepareRun: prepareEvidenceLabelRun,
  buildItemInfo: buildEvidenceItemInfo,
});
